import {useEffect, useState} from 'react';
import {generateHTML, JSONContent} from '@tiptap/react';
import Modal from 'patient-ping-remedy/packages/modal';
import Button, {StyleType} from 'patient-ping-remedy/packages/button';
import Banner, {BannerTypes} from 'patient-ping-remedy/packages/banner';
import Icon, {ICONS} from 'patient-ping-remedy/packages/icon';
import {colors} from 'patient-ping-remedy/packages/theme';
import {css} from '@emotion/css';
import {AdditionalEvent, resolveAdditionalEventDataTypeLabel} from '@/api/dto/care-journey';
import DisplayHelpers from '@/helpers/display_helpers';
import {buildTriggeredByText} from '@/helpers/additional_event_helpers';
import {useAdditionalEventNotes} from '@/hooks/useAdditionalEventNotes';
import {GENERICERROR} from '@manual_encounters/constants/BannerMessages';
import {extensions} from '@common/messages/MessageEditor';
import Loading from '@common/Loading';
import {
  ContextTriggeredBy,
  Divider,
  EmptyState,
  HeaderStack,
  HeaderTitle,
  InlineButtonRow,
  InputFieldSection,
  ModalBody,
  NewNoteLabel,
  NoteAuthor,
  NoteBlock,
  NoteContent,
  NoteDate,
  NoteMeta,
  NotesHeader,
  NotesList,
  NoteTextarea,
  Separator,
} from '@/components/pages/tasks/task_edit/progress/AdditionalEventNotesModal.styles';

type Props = {
  event: AdditionalEvent | null;
  clientId: string;
  isOpen: boolean;
  onClose: () => void;
};

const NOTE_TEXTAREA_ID = 'additional-event-new-note';

const PLACEHOLDER = 'Document context, next steps, re-enrollment plan, or any relevant details...';

// Wrap the textarea text as a minimal tiptap doc so it round-trips through generateHTML.
function buildNoteContent(text: string): JSONContent {
  const trimmed = text.trim();
  return {
    type: 'doc',
    content: [{type: 'paragraph', content: trimmed ? [{type: 'text', text: trimmed}] : []}],
  };
}

export default function AdditionalEventNotesModal({event, clientId, isOpen, onClose}: Props) {
  const [noteText, setNoteText] = useState('');

  const {notes, addNote, isThreadLoading, isThreadError} = useAdditionalEventNotes({event, clientId, isOpen});

  // Remedy's Modal doesn't close on Escape, so wire it up ourselves.
  useEffect(() => {
    if (!isOpen) {
      return;
    }
    const handleKeyDown = (keyEvent: KeyboardEvent) => {
      if (keyEvent.key === 'Escape') {
        onClose();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  // Reset the draft when the modal closes so a re-open starts fresh.
  useEffect(() => {
    if (!isOpen) {
      setNoteText('');
    }
  }, [isOpen]);

  // Block submits until an existing thread finishes loading — the optimistic cache updater needs a
  // cached thread to append to, otherwise the note would silently drop. Also block on a failed load
  // so a thread-less error can't slip past (isThreadLoading is false once the query errors).
  const canSubmit = noteText.trim().length > 0 && !isThreadLoading && !isThreadError;

  const handleAddNote = () => {
    if (!canSubmit) {
      return;
    }
    addNote(buildNoteContent(noteText));
    setNoteText('');
  };

  // Cancel clears the draft, then closes the modal.
  const handleCancel = () => {
    setNoteText('');
    onClose();
  };

  if (!isOpen || !event) {
    return null;
  }

  const dataTypeLabel = resolveAdditionalEventDataTypeLabel(event.dataType);
  // Omit the separator when there's no type label so the title never renders a leading dash.
  const titleText = dataTypeLabel ? `${dataTypeLabel} - Document outreach` : 'Document outreach';

  // Type is in the title, so the sub-line carries only the triggeredBy clause.
  const headerText = (
    <HeaderStack>
      <HeaderTitle>{titleText}</HeaderTitle>
      <ContextTriggeredBy data-testid="additional-event-notes-context">
        {buildTriggeredByText(event)}
      </ContextTriggeredBy>
    </HeaderStack>
  );

  return (
    <Modal
      isOpen={isOpen}
      hideModal={onClose}
      headerText={headerText}
      className={css({width: 'min(85rem, 92vw)'})}
    >
      <ModalBody>
        <InputFieldSection>
          <NewNoteLabel htmlFor={NOTE_TEXTAREA_ID}>New note</NewNoteLabel>
          <NoteTextarea
            id={NOTE_TEXTAREA_ID}
            data-testid="additional-event-note-input"
            value={noteText}
            placeholder={PLACEHOLDER}
            onChange={(changeEvent) => setNoteText(changeEvent.target.value)}
          />
        </InputFieldSection>

        <InlineButtonRow>
          <Button key="cancel" styleType={StyleType.TERTIARY} onClick={handleCancel}>
            Cancel
          </Button>
          <Button key="add-note" styleType={StyleType.PRIMARY} onClick={handleAddNote} disabled={!canSubmit}>
            Add note
          </Button>
        </InlineButtonRow>

        <Divider />

        <NotesHeader>Notes</NotesHeader>
        {isThreadError ? (
          <Banner type={BannerTypes.WARNING}>
            <span data-testid="additional-event-notes-error">Failed to load notes. {GENERICERROR}</span>
          </Banner>
        ) : isThreadLoading ? (
          <div data-testid="additional-event-notes-loading">
            <Loading />
          </div>
        ) : notes.length === 0 ? (
          <EmptyState data-testid="additional-event-notes-empty">No notes yet.</EmptyState>
        ) : (
          <NotesList data-testid="additional-event-notes-list">
            {notes.map((note) => (
              <NoteBlock key={note.id} data-message-id={note.id}>
                <NoteContent dangerouslySetInnerHTML={{__html: generateHTML(note.content, extensions)}} />
                <NoteMeta>
                  <Icon
                    iconClass={ICONS['user-check']}
                    iconSize="sm"
                    weight="far"
                    color={colors.gray5}
                    aria-hidden="true"
                  />
                  <NoteAuthor>
                    {DisplayHelpers.formatName(note.user.first_name, null, note.user.last_name)}
                  </NoteAuthor>
                  <Separator aria-hidden="true">·</Separator>
                  <NoteDate>{DisplayHelpers.formatDateTimeLong(note.createdAt)}</NoteDate>
                </NoteMeta>
              </NoteBlock>
            ))}
          </NotesList>
        )}
      </ModalBody>
    </Modal>
  );
}
